"use client";

import { useEffect, useState } from "react";
import { SuperLink as Link } from "@/components/common/super-link";
import { ClientContentBlocks } from "@/components/common/content-blocks/client-content-blocks";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Quiz } from "@/components/common/quiz/quiz";
import { Spinner } from "@/components/ui/spinner";
import { scrollToPos } from "@/service/scroll/scroll";
import { CourseChapterTestProgressIndicator } from "./course-chapter-test-progress-indicator";

function hasAssessment(step: AssessmentStep) {
  return step.content.some((block) => block.type === "quiz");
}

export const CourseChapterTest = ({
  steps = [],
  name,
  category,
  onFinish,
  onNextStep,
  onPreviousStep,
  scroll = true,
}: {
  steps: AssessmentStep[];
  name?: string;
  category?: { href: string; name: string };
  onFinish: () => void;
  onNextStep?: () => void;
  onPreviousStep?: () => void;
  scroll?: boolean;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [canAdvance, setCanAdvance] = useState(true);
  const [currentContent, setCurrentContent] = useState<AssessmentStep>(
    steps[currentStep],
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentContent(steps[currentStep]);
    if (scroll) {
      scrollToPos({
        top: Math.max(0, 112),
        behavior: "smooth",
      });
    }

    if (!hasAssessment(steps[currentStep])) {
      setCanAdvance(true);
    }
  }, [currentStep, steps]);

  return (
    <div className="flex w-full flex-col items-center justify-center md:mt-16">
      <div className="flex w-full flex-col justify-between overflow-hidden rounded-xl border bg-white p-4 shadow md:min-h-[500px] md:p-8">
        <div>
          {category && (
            <Link
              href={category.href}
              className="text-remedium-blue pb-2 text-xs font-medium uppercase hover:underline"
            >
              {category.name}
            </Link>
          )}
          <CourseChapterTestProgressIndicator
            currentStep={currentStep}
            totalSteps={steps.length}
          />
          <h1
            className="pt-8 pb-6 text-lg"
            dangerouslySetInnerHTML={{ __html: currentContent.title }}
          ></h1>
          {currentContent.content.map((block, index) =>
            block.type === "quiz" ? (
              <Quiz
                key={block.data.uuid}
                quiz={block.data}
                setCanAdvance={setCanAdvance}
                stepIdx={index}
                showHeading={false}
                completedEvent={`course_quiz_step`}
              />
            ) : (
              <ClientContentBlocks
                key={index}
                blocks={[block]}
                theme="clinical_case"
              />
            ),
          )}
        </div>
        <div>
          <div className="my-8 flex justify-between">
            <Button
              variant={"outline"}
              onClick={() => {
                onPreviousStep?.();
                setCurrentStep((prev) => Math.max(prev - 1, 0));
              }}
              disabled={currentStep === 0 || isLoading}
            >
              Wstecz
            </Button>
            <Button
              variant={"blue"}
              disabled={!canAdvance || isLoading}
              onClick={async () => {
                if (currentStep < steps.length - 1) {
                  onNextStep?.();
                  setCurrentStep((prev) => prev + 1);
                } else {
                  setIsLoading(true);
                  await onFinish();
                  setIsLoading(false);
                }
              }}
            >
              {isLoading && <Spinner />}
              {currentStep < steps.length - 1 ? "Dalej" : "Zakończ"}
            </Button>
          </div>
          {name && (
            <div className="relative flex h-[20px] w-full items-center justify-center">
              <Separator className="w-full" />
              <p className="text-muted-foreground absolute top-0 left-0 flex h-full w-full items-center justify-center bg-transparent text-center text-sm">
                <span className="max-w-[380px] bg-white px-2 text-center">
                  {name}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
